import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Pagination,
  IconButton,
  Chip as MuiChip,
  Stack,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../services/api';
import { TimeEntry, User, Campaign, Department } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { exportToCSV, exportToExcel, exportToPDF, ExportData } from '../utils/exportUtils';
import { TimezoneService } from '../services/timezoneService';
import { CampaignFilterService } from '../services/campaignFilterService';
import moment from 'moment-timezone';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';

interface ReportFilters {
  startDate: string;
  endDate: string;
  userId: string;
  departmentId: string;
  campaignId: string;
  entryType: string;
  page: number;
  limit: number;
}

interface ReportResponse {
  success: boolean;
  data: TimeEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const TimeRecordsReportPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    userId: '',
    departmentId: '',
    campaignId: '',
    entryType: '',
    page: 1,
    limit: 50,
  });
  const [showFilters, setShowFilters] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Check if user can share reports (supervisor and above)
  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<'employee' | 'campaign' | 'entryType' | 'timestamp'>('timestamp');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc'); // default newest first

  // Get users, departments, and campaigns for filter dropdowns (only for roles that can see multiple users/campaigns)
  const { data: usersData } = useQuery<{ data: { success: boolean; data: User[] } }, Error>({
    queryKey: ['users'],
    queryFn: apiClient.users.getAll,
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  const { data: departmentsData } = useQuery<{ data: { success: boolean; data: Department[] } }, Error>({
    queryKey: ['departments'],
    queryFn: apiClient.departments.getAll,
    enabled: ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  const { data: campaignsData } = useQuery<{ data: { success: boolean; data: Campaign[] } }, Error>({
    queryKey: ['campaigns', filters.startDate, filters.endDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: filters.startDate,
      includeWithActivityTo: filters.endDate,
    }),
    enabled: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Filter departments and campaigns based on user role and assigned campaigns
  const availableDepartments = departmentsData?.data.data ? 
    (user && ['team_lead', 'supervisor'].includes(user.role) ? 
      departmentsData.data.data.filter(dept => dept.id === user.departmentId) :
      departmentsData.data.data) : [];

  const availableCampaigns = campaignsData?.data.data ? 
    (user ? CampaignFilterService.getUserAccessibleCampaigns(user, campaignsData.data.data) : []) : [];

  // Get report data
  const { data: reportResponse, isLoading, error, refetch } = useQuery<ReportResponse>({
    queryKey: ['timeRecordsReport', filters],
    queryFn: async () => {
      const response = await apiClient.timeEntries.getReport(filters);
      return response.data;
    },
    enabled: true,
  });

  const handleFilterChange = (field: keyof ReportFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : (typeof value === 'number' ? value : prev.page), // Reset to page 1 when other filters change
    }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('userId');
    const date = params.get('date');
    if (userId || date) {
      setFilters(prev => ({
        ...prev,
        userId: userId || prev.userId,
        startDate: date || prev.startDate,
        endDate: date || prev.endDate,
        page: 1,
      }));
    }
  }, [location.search]);

  const handleSearch = () => {
    refetch();
  };

  const handleReset = () => {
    setFilters({
      startDate: moment().subtract(2, 'weeks').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      userId: '',
      departmentId: '',
      campaignId: '',
      entryType: '',
      page: 1,
      limit: 50,
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    handleFilterChange('page', page);
  };

  const getEntryTypeColor = (entryType: string) => {
    switch (entryType) {
      case 'clock_in':
        return 'success';
      case 'clock_out':
        return 'error';
      case 'break_start':
        return 'warning';
      case 'break_end':
        return 'info';
      case 'bathroom_start':
        return 'warning';
      case 'bathroom_end':
        return 'info';
      case 'lunch_start':
        return 'secondary';
      case 'lunch_end':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatEntryType = (entryType: string) => {
    return entryType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTimestamp = (entry: any) => {
    // Always use backend-formatted timestamp if available (it's already in employee's timezone)
    if (entry.timestampFormatted) {
      return entry.timestampFormatted;
    }
    
    // If backend didn't provide formatted timestamp, format on frontend
    // Get the employee's timezone (the person whose record this is), not the viewer's timezone
    const employeeTimezone = entry.user?.timezone || user?.timezone || TimezoneService.getUserTimezone();
    
    // Ensure timestamp is treated as UTC
    const rawTimestamp = entry.timestamp instanceof Date 
      ? entry.timestamp.toISOString() 
      : (typeof entry.timestamp === 'string' 
          ? (entry.timestamp.includes('T') && entry.timestamp.includes('Z') 
              ? entry.timestamp 
              : new Date(entry.timestamp).toISOString())
          : new Date(entry.timestamp).toISOString());
    
    // Format using employee's timezone
    return moment.utc(rawTimestamp).tz(employeeTimezone).format('YYYY-MM-DD HH:mm:ss A');
  };

  const canViewMultipleUsers = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');
  const canViewMultipleCampaigns = ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');

  // Export functions
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const fetchAllTimeRecordsForExport = async () => {
    const baseParams = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      userId: filters.userId || undefined,
      departmentId: filters.departmentId || undefined,
      campaignId: filters.campaignId || undefined,
      entryType: filters.entryType || undefined,
      page: 1,
      limit: 1000,
    };

    let allRecords: TimeEntry[] = [];
    let page = 1;
    let total = 0;

    while (true) {
      const response = await apiClient.timeEntries.getReport({
        ...baseParams,
        page
      });
      const pageData = response.data.data || [];
      const pagination = response.data.pagination;
      allRecords = allRecords.concat(pageData);
      total = pagination?.total ?? allRecords.length;

      if (allRecords.length >= total || pageData.length === 0) {
        break;
      }
      page += 1;
    }

    return allRecords;
  };

  const handleExport = async (format: 'csv' | 'excel' | 'pdf') => {
    try {
      const data = await fetchAllTimeRecordsForExport();
      const exportData: ExportData = {
        data,
        filters: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          userId: filters.userId,
          departmentId: filters.departmentId,
          campaignId: filters.campaignId,
          entryType: filters.entryType,
        },
        reportTitle: 'Time Records Report',
      };

      switch (format) {
        case 'csv':
          exportToCSV(exportData);
          break;
        case 'excel':
          exportToExcel(exportData);
          break;
        case 'pdf':
          exportToPDF(exportData);
          break;
      }
    } catch (error) {
      console.error('Error exporting time records:', error);
      alert('Failed to export. Please try again.');
    } finally {
      handleExportMenuClose();
    }
  };

  const sortedData = React.useMemo(() => {
    if (!reportResponse?.data) return [] as TimeEntry[];
    const copy = [...reportResponse.data];
    copy.sort((a: any, b: any) => {
      let av: any = '';
      let bv: any = '';
      switch (sortBy) {
        case 'employee':
          const aDisplayUser = (a as any).displayUser || a.user;
          const bDisplayUser = (b as any).displayUser || b.user;
          av = `${aDisplayUser?.lastName || ''}${aDisplayUser?.firstName || ''}`.toLowerCase();
          bv = `${bDisplayUser?.lastName || ''}${bDisplayUser?.firstName || ''}`.toLowerCase();
          break;
        case 'campaign':
          av = (a.campaign?.name || '').toLowerCase();
          bv = (b.campaign?.name || '').toLowerCase();
          break;
        case 'entryType':
          av = a.entryType;
          bv = b.entryType;
          break;
        case 'timestamp':
        default:
          av = new Date(a.timestamp).getTime();
          bv = new Date(b.timestamp).getTime();
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [reportResponse?.data, sortBy, sortDir]);

  const toggleSort = (col: 'employee' | 'campaign' | 'entryType' | 'timestamp') => {
    if (sortBy === col) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'timestamp' ? 'desc' : 'asc');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Time Records Report
        </Typography>
        <Box>
          <IconButton onClick={() => setShowFilters(!showFilters)} color="primary">
            <FilterIcon />
          </IconButton>
          <IconButton onClick={() => refetch()} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {canViewMultipleUsers && (
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={usersData?.data.data || []}
                    value={filters.userId ? usersData?.data.data.find((user: User) => user.id === filters.userId) || null : null}
                    onChange={(event, newValue) => {
                      handleFilterChange('userId', newValue?.id || '');
                    }}
                    getOptionLabel={(option) => option ? `${option.firstName} ${option.lastName} (${option.employeeId})` : ''}
                    renderInput={(params) => (
                      <TextField {...params} label="Employee" placeholder="Search employee..." />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.firstName} {option.lastName} ({option.employeeId})
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="No employees found"
                    clearOnEscape
                    selectOnFocus
                    handleHomeEndKeys
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const searchLower = inputValue.toLowerCase();
                      return options.filter(option =>
                        `${option.firstName} ${option.lastName}`.toLowerCase().includes(searchLower) ||
                        (option.employeeId && option.employeeId.toLowerCase().includes(searchLower))
                      );
                    }}
                  />
                </Grid>
              )}
              {canViewMultipleUsers && (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Department"
                    value={filters.departmentId}
                    onChange={(value) => handleFilterChange('departmentId', String(value))}
                    options={availableDepartments.map((department: Department) => ({
                      value: department.id,
                      label: department.name,
                    }))}
                  />
                </Grid>
              )}
              {canViewMultipleCampaigns && (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableSelect
                    label="Campaign"
                    value={filters.campaignId}
                    onChange={(value) => handleFilterChange('campaignId', String(value))}
                    options={availableCampaigns.map((campaign: Campaign) => ({
                      value: campaign.id,
                      label: campaign.name,
                    }))}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Entry Type"
                  value={filters.entryType}
                  onChange={(value) => handleFilterChange('entryType', String(value))}
                  options={[
                    { value: 'clock_in', label: 'Clock In' },
                    { value: 'clock_out', label: 'Clock Out' },
                    { value: 'break_start', label: 'Break Start' },
                    { value: 'break_end', label: 'Break End' },
                    { value: 'lunch_start', label: 'Lunch Start' },
                    { value: 'lunch_end', label: 'Lunch End' },
                    { value: 'bathroom_start', label: 'Bathroom Start' },
                    { value: 'bathroom_end', label: 'Bathroom End' },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Records per page"
                  value={filters.limit}
                  onChange={(value) => handleFilterChange('limit', Number(value))}
                  options={[25, 50, 100, 200].map((value) => ({
                    value,
                    label: String(value),
                  }))}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={isLoading}
              >
                Search
              </Button>
              <Button variant="outlined" onClick={handleReset}>
                Reset
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={handleExportClick}
                disabled={!reportResponse?.data || reportResponse.data.length === 0}
              >
                Export
              </Button>
              {canShareReports && (
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => setShareDialogOpen(true)}
                  disabled={!reportResponse?.data || reportResponse.data.length === 0}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Share
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => handleExport('csv')}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('excel')}>
          <ListItemIcon>
            <ExcelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('pdf')}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* Active Filters */}
      {(() => {
        const activeFilters = Object.entries(filters).filter(([key, value]) => 
          value !== '' && value !== 0 && key !== 'page' && key !== 'limit'
        );
        
        if (activeFilters.length === 0) return null;
        
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Active Filters:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {activeFilters.map(([key, value]) => {
                let label = '';
                let displayValue = value;
                
                switch (key) {
                  case 'startDate':
                    label = 'Start Date';
                    break;
                  case 'endDate':
                    label = 'End Date';
                    break;
                  case 'userId':
                    label = 'User';
                    const user = usersData?.data.data.find((u: User) => u.id === value);
                    displayValue = user ? `${user.firstName} ${user.lastName}` : value;
                    break;
                  case 'departmentId':
                    label = 'Department';
                    const department = availableDepartments.find((d: Department) => d.id === value);
                    displayValue = department ? department.name : value;
                    break;
                  case 'campaignId':
                    label = 'Campaign';
                    const campaign = availableCampaigns.find((c: Campaign) => c.id === value);
                    displayValue = campaign ? campaign.name : value;
                    break;
                  case 'entryType':
                    label = 'Entry Type';
                    displayValue = formatEntryType(value as string);
                    break;
                  default:
                    label = key;
                }
                
                return (
                  <MuiChip
                    key={key}
                    label={`${label}: ${displayValue}`}
                    onDelete={() => handleFilterChange(key as keyof ReportFilters, '')}
                    deleteIcon={<CloseIcon />}
                    size="small"
                  />
                );
              })}
            </Stack>
          </Box>
        );
      })()}

      {/* Results */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Time Records
              {reportResponse?.pagination && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({reportResponse.pagination.total} total records)
                </Typography>
              )}
            </Typography>
          </Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load time records report
            </Alert>
          )}

          {reportResponse && !isLoading && (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell onClick={() => toggleSort('employee')} sx={{ cursor: 'pointer' }}>
                        Employee {sortBy === 'employee' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                      <TableCell onClick={() => toggleSort('campaign')} sx={{ cursor: 'pointer' }}>
                        Campaign {sortBy === 'campaign' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                      <TableCell onClick={() => toggleSort('entryType')} sx={{ cursor: 'pointer' }}>
                        Entry Type {sortBy === 'entryType' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                      <TableCell onClick={() => toggleSort('timestamp')} sx={{ cursor: 'pointer' }}>
                        Timestamp {sortBy === 'timestamp' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                      </TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedData.map((entry: TimeEntry) => {
                      // Show the actual person who worked (for payroll)
                      const displayUser = (entry as any).displayUser || (entry as any).user;
                      const isCoverage = (entry as any).isCoverage || false;
                      const coveredEmployee = (entry as any).coveredEmployee; // The person being covered
                      
                      return (
                        <TableRow key={entry.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {displayUser?.displayName || `${displayUser?.firstName || ''} ${displayUser?.lastName || ''}`.trim()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {displayUser?.employeeId} • {displayUser?.role}
                                  {isCoverage && coveredEmployee && (
                                    <span> • Covering for: {coveredEmployee.displayName || `${coveredEmployee.firstName} ${coveredEmployee.lastName}`}</span>
                                  )}
                                </Typography>
                              </Box>
                              {isCoverage && (
                                <Tooltip title={`This employee is covering for ${coveredEmployee?.displayName || 'another employee'}. This time entry is for payroll purposes and shows the actual worker.`}>
                                  <PersonAddIcon 
                                    color="primary" 
                                    fontSize="small" 
                                    sx={{ 
                                      cursor: 'help',
                                      flexShrink: 0
                                    }} 
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(entry as any).campaign?.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatEntryType(entry.entryType)}
                            color={getEntryTypeColor(entry.entryType) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatTimestamp(entry)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {entry.notes || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {reportResponse.pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={reportResponse.pagination.totalPages}
                    page={reportResponse.pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}

          {reportResponse && reportResponse.data.length === 0 && !isLoading && (
            <Alert severity="info">
              No time records found matching the selected criteria.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Share Report Dialog */}
      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="time-records"
          reportFilters={filters}
          reportTitle="Time Records Report"
          onShareSuccess={() => {
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default TimeRecordsReportPage;
